import { useFlags } from 'launchdarkly-react-client-sdk';

import type { Role } from 'datocms-plugin-sdk';

export const DEFAULT_SCENE_MANIPULATION_LIMIT = 100;

/** Default cap (minutes) for voice/music separation (PLAT-10626). */
export const DEFAULT_STEM_SEPARATION_MAX_MINUTES = 20;

/**
 * Default cap (minutes) on the length of video a dub run may cover (PLAT-11532).
 * Dub generation is one synchronous request with a fixed wall-clock budget, so
 * long stories time out; the number is a product agreement, not a measured
 * limit, and lives in LaunchDarkly so it can move without a deploy.
 */
export const DEFAULT_DUBBING_MAX_MINUTES = 10;

export function useFlagsCombination(currentRole: Role) {
  const isDatoUser =
    currentRole?.attributes && currentRole.attributes.name !== 'Unknown';
  const ldFlags = useFlags();

  // Empty payload = LaunchDarkly's initial fetch hasn't landed, so these are
  // transient defaults, not real values. Must derive from `useFlags()` to stay
  // reactive: the previous `ldClient.getContext().anonymous` check was read
  // during render, and `identify()` re-renders the provider only when a flag
  // value actually changes — so it never flipped for users whose values match
  // the anonymous evaluation.
  const initialized = Object.keys(ldFlags).length > 0;

  const {
    contentStudioEnableSharing,
    contentStudioEnableClipProduceBtn,
    contentStudioEnableClipPublishBtn,
    contentStudioEnableEmailRegenerationBtn,
    contentStudioEnableShareableImageRegeneration,
    contentStudioEnableBlogRegeneration,
    contentStudioEnableTalkingPointsRegenerationBtn,
    contentStudioEnableEmailShareBtn,
    contentStudioEnableBlogShareBtn,
    contentStudioEnableShareableImageShareBtn,
    contentStudioEnableClipShareBtn,
    contentStudioEnableBlogSaveBtn,
    contentStudioEnableEmailSaveBtn,
    contentStudioEnableShareableImageSaveBtn,
    contentStudioEnableTalkingPointsSaveBtn,
    contentStudioEnableClipEdit,
    contentStudioEnableAiContentLanguageSelection,
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    creatorStudioEnableVideoTemplatesTab,
    isEnterprisePlatform,
    enableCreatorStudio,
    enableCreatorStudioAccess,
    hideStoryDashboardNav,
    enableImportXml,
    enableFixTranscriptButton,
    storyManagerEnableAddStories,
    renderUsingChunks,
    enableLargeFileSizeUpload,
    enableNoLimitFileSizeUpload,
    enableStoryVideoSilenceCheck,
    enableStackedLayouts,
    enableTimelineSnapGuide,
    enableTimelineSnapping,
    enableExperimentalTimelineFrameThumbnails,
    enableTranscriptEditor,
    enableStoryTranscriptEditing,
    enableMultiFileConcat,
    removeTinyFragmentsFromSource,
    enableSpeakersIdentification,
    enableTranscriptProviderSelector,
    enableClipThemeGeneration,
    enableUrlUpload,
    enableZoomConnection,
    storyManagerEnableUnifiedUploadProgress,
    enableGenerateAiPhoto,
    enablePylonChatBubble,

    // New S3 resources upload paths
    // Enables dual write to S3 using signed urls and API upload
    enableVideoResourcesS3DualWrite,
    // Enables dual write transcript snapshots to S3 and Dato API
    enableTranscriptSnapshotResourceDualWrite,
    // Enables upload of transcript snapshots to S3 with VideoResourcesProvider
    enableTranscriptSnapshotResourceUpload,
    // Enables upload ofvideo source to S3 using signed urls
    enableVideoResourcesS3Upload,
    enableParallelClipSave,
    enableLightweightClipSave,
    enableBackgroundClipSave,
    enableWriteBehindCacheClipProducer,
    enableKaraokeDualWrite,
    transcriptionProviders,

    enableDetectScenesButton,
    enableFramingTab,
    enableAutoFraming,
    autoFramingStrategy,
    enableShareableImageCompositions,
    enableShareableImageSaveUpdateTemplate,
    enableCompositionSaveUpdateTemplate,
    enableApplyStandardsPreview,
    sceneManipulationLimit,
    enableContentStudioClipEditPreview,
    homeEnableQuickActions,
    contentStudioModalEnableThumbnail,

    // New feature flags managed by OpenTofu
    appEnableApprovedButton,
    contentStudioAiEnableCustom,
    contentStudioClipProducerEnableCustom,
    contentStudioClipProducerEnableTextVerification,
    contentStudioClipProducerFilterTranscriptBySpeaker,
    contentStudioClipProducerChunkSize,
    contentStudioContentProducingEnableCustom,
    contentStudioDashboardEnableAnalytics,
    contentStudioEmailEnableTab,
    contentStudioMediaEnableTab,
    contentStudioModalEnableAnalytics,
    contentStudioModalEnableAutoFrameBySpeaker,
    contentStudioModalEnableScenesStrip,
    contentStudioModalEnableScenesStripInPlayer,
    contentStudioModalEnableShare,
    contentStudioModalEnableShareButtonDropdown,
    contentStudioQuotesEnableGlobalTemplates,
    contentStudioTimelineEnableGapModeRippleEdit,
    contentStudioTimelineEnableLockFrame,
    contentStudioTalkingPointsEnableTab,
    contentStudioTranscriptEnableTab,
    contentStudioTranscriptSpeakersEnableTab,
    contentStudioTranscriptSpeakersEnableTopicFiltering,
    contentStudioTranscriptSpeakersEnableClipChat,
    contentStudioCaptionsEnableTab,
    creatorStudioAiEnableTab,
    creatorStudioAiProducerEnableClipProducerOnly,
    creatorStudioAiProducerEnablePhotoTab,
    creatorStudioCaptionsEnableArborTemplates,
    creatorStudioMediaEnableTab,
    creatorStudioTimelineEnableFit13,
    creatorStudioTimelineEnableFitFill,
    creatorStudioTimelineEnableFitSplitHorizontal11,
    creatorStudioTimelineEnableFitSplitHorizontal13,
    creatorStudioTimelineEnableFitSplitVertical11,
    creatorStudioTimelineEnableFitZoomCall,
    creatorStudioTimelineEnableLockFraming,
    creatorStudioTimelineEnableReframingDropdown,
    creatorStudioSettingsAnimationAndAudioFadeOnly,
    creatorStudioFontsHideArborFonts,
    homeEnableDefaultLanding,
    storyManagerAddStoriesEnableButtons,
    enableClipProducerVideoTemplateSelection,
    enableMovingBetweenTracks,
    contentGenerationChatGptModel,
    contentStudioQuotesHide169And916,
    contentStudioModalEnableTranscript,
    contentStudioModalEnableComments,
    contentStudioEnableCommentDrawing,
    enableRendererLayoutReorder,
    enableTimelineCopyPaste,
    contentStudioEnableBlockTempClipEditing,
    contentStudioPullQuotesEnablePhotoSources,
    enablePostHogAiObservability,
    contentStudioContentProducingEnableGenerate,
    creatorStudioLayoutStrategy,
    creatorStudioTimelineWindowingConfig,
    enableClipProducerReels,
    homeActionsEnableEmailButton,
    homeActionsEnableEditVideoButton,
    homeActionsEnableAddStoryButton,
    creatorStudioTranscriptAddPhoto,
    contentStudioTranscriptAddPhoto,
    enableTranscriptOverlayAdd,
    enableTranscriptOverlayRangeAdjust,
    enableTranslationReview,
    enableTranslationReviewerMode,
    contentStudioEnableTranslatedUndoRedo,
    enableWhatsNewChangelog,
    enableSettingsScreen,
    enableAiVoices,
    enableDubVoiceSettings,
    enableAutoRedubOnEdit,
    enableTranslationMemory,
    enableStemSplitterPicker,
    stemSeparationMaxMinutes,
    dubbingMaxMinutes,
    enableStoryFolders,
    enableSpeakerManager,
    lockSceneBoundElements,
    enableSafeZones,
    videoTemplatesHideSettings,
    enableTemplateLockPosition,
    contentStudioEnableAutosave,
    enableCompositionCanvasQuickEdit,
    contentStudioEnableClipStatusAssignee,
    enableBasicReelCreation,
    enableBulkClipDelete,
    enableReelTranscriptStructure,
  } = ldFlags;

  const enableB2B = isEnterprisePlatform || isDatoUser;

  function resolveB2BFlag(flag: 'on' | 'off' | 'unset') {
    if (isDatoUser) return true;
    if (flag === 'off') return false;
    return flag === 'on' || enableB2B;
  }

  function resolveAdminFlag(...flags: Array<'on' | 'off' | 'unset'>) {
    return (
      isDatoUser ||
      (flags.some(flag => flag === 'on') && !flags.some(flag => flag === 'off'))
    );
  }

  // Translation Reviewer reduced experience (targeted by email/segment in LD):
  // a stripped-down mode where the user can only review a clip's transcript
  // (correcting translations + speaker labels). It force-disables clip
  // editing/producing/publishing/sharing below so surfaces already gated on
  // those flags disappear with no further changes.
  const reviewerMode = enableTranslationReviewerMode ?? false;

  return {
    initialized,
    enableSharing: resolveB2BFlag(contentStudioEnableSharing),
    contentStudioEnableClipProducer:
      !reviewerMode && resolveB2BFlag(contentStudioEnableClipProduceBtn),
    contentStudioEnableClipPublish:
      !reviewerMode && resolveB2BFlag(contentStudioEnableClipPublishBtn),
    contentStudioEnableEmailShare: resolveB2BFlag(
      contentStudioEnableEmailShareBtn,
    ),
    contentStudioEnableBlogShare: resolveB2BFlag(
      contentStudioEnableBlogShareBtn,
    ),
    contentStudioEnableImageShare: resolveB2BFlag(
      contentStudioEnableShareableImageShareBtn,
    ),
    contentStudioEnableClipShare:
      !reviewerMode && resolveB2BFlag(contentStudioEnableClipShareBtn),
    contentStudioEnableBlogSave: resolveB2BFlag(contentStudioEnableBlogSaveBtn),
    contentStudioEnableEmailSave: resolveB2BFlag(
      contentStudioEnableEmailSaveBtn,
    ),
    contentStudioEnableAutosave: contentStudioEnableAutosave ?? false,
    contentStudioEnableShareableImageSave: resolveB2BFlag(
      contentStudioEnableShareableImageSaveBtn,
    ),
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    creatorStudioEnableVideoTemplatesTab, //: true,
    isEnterprisePlatform: enableB2B || false,
    isDatoUser,
    renderUsingChunks,
    enableCreatorStudioNav:
      !reviewerMode && (isDatoUser || enableCreatorStudioAccess === true),
    storyManagerEnableAddStoriesButton: resolveAdminFlag(
      enableCreatorStudio,
      storyManagerEnableAddStories,
    ),
    hideStoryDashboardNav: hideStoryDashboardNav || false,
    enableBlogRegeneration: resolveB2BFlag(contentStudioEnableBlogRegeneration),
    enableEmailRegeneration: resolveB2BFlag(
      contentStudioEnableEmailRegenerationBtn,
    ),
    enableShareableImageRegeneration: resolveB2BFlag(
      contentStudioEnableShareableImageRegeneration,
    ),
    enableCaptionTemplatesTab: false,
    enableVideoClipEdit:
      !reviewerMode && resolveAdminFlag(contentStudioEnableClipEdit),
    enableTalkingPointsStudioSave: resolveB2BFlag(
      contentStudioEnableTalkingPointsSaveBtn,
    ),
    enableTalkingPointsStudioRegeneration: resolveB2BFlag(
      contentStudioEnableTalkingPointsRegenerationBtn,
    ),
    enableImportXml: resolveAdminFlag(enableImportXml),
    enableFixTranscriptButton: resolveAdminFlag(enableFixTranscriptButton),
    enableLargeFileSizeUpload,
    enableNoLimitFileSizeUpload,
    enableStoryVideoSilenceCheck: enableStoryVideoSilenceCheck ?? false,
    enableTimelineSnapGuide,
    enableTimelineSnapping,
    enableExperimentalTimelineFrameThumbnails,
    enableStackedLayouts,
    enableTranscriptEditor,
    // Story-level transcript editing (Replace Term / punctuation on the
    // Transcript & Speakers tab and the Review workspace's original pane).
    // Off by default: a word-count change there renumbers positions that
    // existing translations and clip captions still rely on. See
    // `storyTranscriptEditLock.ts`. Turn on per target in LaunchDarkly.
    enableStoryTranscriptEditing: enableStoryTranscriptEditing ?? false,
    enableMultiFileConcat,
    removeTinyFragmentsFromSource: removeTinyFragmentsFromSource || false,
    contentStudioEnableAIContentLanguageSelection:
      contentStudioEnableAiContentLanguageSelection,
    enableSpeakersIdentification,
    enableTranscriptProviderSelector: resolveAdminFlag(
      enableTranscriptProviderSelector,
    ),
    enableClipThemeGeneration,
    enableUrlUpload: enableUrlUpload || false,
    enableZoomConnection: enableZoomConnection || false,
    enableTranscriptDualWrite: true,
    enableTranscriptMigrationSizeLimit: true,
    // New S3 resources upload paths
    // Enables dual write to S3 using signed urls and API upload
    enableVideoResourcesS3DualWrite: enableVideoResourcesS3DualWrite ?? false,
    // Enables dual write transcript snapshots to S3 and Dato API
    enableTranscriptSnapshotResourceDualWrite:
      enableTranscriptSnapshotResourceDualWrite ?? false,
    // Enables upload of transcript snapshots to S3 with VideoResourcesProvider
    enableTranscriptSnapshotResourceUpload:
      (enableTranscriptSnapshotResourceDualWrite ||
        enableTranscriptSnapshotResourceUpload) ??
      false,
    // Enables upload ofvideo source to S3 using signed urls
    enableVideoResourcesS3Upload:
      (enableVideoResourcesS3DualWrite || enableVideoResourcesS3Upload) ??
      false,
    enableParallelClipSave: enableParallelClipSave ?? false,
    enableLightweightClipSave: enableLightweightClipSave ?? false,
    enableBackgroundClipSave: enableBackgroundClipSave ?? false,
    enableWriteBehindCacheClipProducer:
      enableWriteBehindCacheClipProducer ?? false,
    storyManagerEnableUnifiedUploadProgress:
      storyManagerEnableUnifiedUploadProgress || false,
    enableNewKaraokeArchitecture: true,
    enableKaraokeDualWrite: enableKaraokeDualWrite ?? true,
    enableGenerateAiPhoto: enableGenerateAiPhoto || false,
    enablePylonChatBubble: enablePylonChatBubble === 'on' || false,
    transcriptionProviders: transcriptionProviders || [
      'rev',
      'aws',
      'assembly',
    ],
    enableDetectScenesButton: enableDetectScenesButton || false,
    enableAutoFraming: enableAutoFraming || false,
    autoFramingStrategy:
      autoFramingStrategy === 'basic' ? 'basic' : 'transcript-active-speaker',
    enableShareableImageCompositions: enableShareableImageCompositions || false,
    enableShareableImageSaveUpdateTemplate:
      enableShareableImageSaveUpdateTemplate || false,
    enableCompositionSaveUpdateTemplate:
      enableCompositionSaveUpdateTemplate || false,
    enableApplyStandardsPreview: enableApplyStandardsPreview || false,
    sceneManipulationLimit:
      typeof sceneManipulationLimit === 'number'
        ? sceneManipulationLimit
        : DEFAULT_SCENE_MANIPULATION_LIMIT,
    enableContentStudioClipEditPreview:
      enableContentStudioClipEditPreview || false,
    homeEnableDefaultLanding: homeEnableDefaultLanding ?? true,
    enableHomeQuickActions: homeEnableQuickActions ?? true,
    contentStudioModalEnableThumbnail:
      contentStudioModalEnableThumbnail ?? true,

    // New feature flags managed by OpenTofu
    appEnableApprovedButton: appEnableApprovedButton ?? true,
    contentStudioAiEnableCustom: contentStudioAiEnableCustom ?? true,
    contentStudioClipProducerEnableCustom:
      contentStudioClipProducerEnableCustom ?? true,
    contentStudioClipProducerEnableTextVerification:
      contentStudioClipProducerEnableTextVerification ?? false,
    contentStudioClipProducerFilterTranscriptBySpeaker:
      contentStudioClipProducerFilterTranscriptBySpeaker ?? true,
    contentStudioClipProducerChunkSize:
      typeof contentStudioClipProducerChunkSize === 'number'
        ? contentStudioClipProducerChunkSize
        : 2000,
    contentStudioContentProducingEnableCustom:
      contentStudioContentProducingEnableCustom ?? true,
    contentStudioDashboardEnableAnalytics:
      contentStudioDashboardEnableAnalytics ?? true,
    contentStudioEmailEnableTab: contentStudioEmailEnableTab ?? true,
    contentStudioMediaEnableTab: contentStudioMediaEnableTab ?? true,
    contentStudioModalEnableAnalytics:
      contentStudioModalEnableAnalytics ?? true,
    // Reframe modal's one-click "scenes per speaker + auto-frame" action.
    contentStudioModalEnableAutoFrameBySpeaker:
      contentStudioModalEnableAutoFrameBySpeaker ?? false,
    // Master gate for the read-only scenes strip (PLAT-11234). Off means the
    // clip preview and the reframe modal are exactly as they are today.
    // Defaults `false` (dark launch).
    contentStudioModalEnableScenesStrip:
      contentStudioModalEnableScenesStrip ?? false,
    // Sub-gate for the strip in the clip preview's control bar, and with it the
    // row height held for that strip in every playback mode. Off — the default
    // — leaves the strip in the reframe modal only, where it is the way out of
    // "No video in this section". Has no effect unless the master gate is on.
    contentStudioModalEnableScenesStripInPlayer:
      contentStudioModalEnableScenesStripInPlayer ?? false,
    contentStudioModalEnableShare: contentStudioModalEnableShare ?? true,
    contentStudioModalEnableShareButtonDropdown:
      contentStudioModalEnableShareButtonDropdown ?? true,
    contentStudioQuotesEnableGlobalTemplates:
      contentStudioQuotesEnableGlobalTemplates ?? true,
    contentStudioTimelineEnableGapModeRippleEdit:
      contentStudioTimelineEnableGapModeRippleEdit ?? true,
    contentStudioTimelineEnableLockFrame:
      contentStudioTimelineEnableLockFrame ?? true,
    contentStudioTalkingPointsEnableTab:
      contentStudioTalkingPointsEnableTab ?? true,
    contentStudioTranscriptEnableTab: contentStudioTranscriptEnableTab ?? true,
    // Translation reviewers also get the Transcript & Speakers tab (in a
    // reduced, transcript-only form — see TranscriptSpeakersContent).
    contentStudioTranscriptSpeakersEnableTab:
      reviewerMode || (contentStudioTranscriptSpeakersEnableTab ?? false),
    contentStudioTranscriptSpeakersEnableTopicFiltering:
      contentStudioTranscriptSpeakersEnableTopicFiltering ?? false,
    // PLAT-11806: the Find Clips chat tab in the right-hand column. Off until
    // the custom clip preset is confirmed to return impact scores — without
    // them "best first" is just "first returned".
    contentStudioTranscriptSpeakersEnableClipChat:
      contentStudioTranscriptSpeakersEnableClipChat ?? false,
    contentStudioCaptionsEnableTab: contentStudioCaptionsEnableTab ?? false,
    creatorStudioAiEnableTab: creatorStudioAiEnableTab ?? true,
    creatorStudioAiProducerEnableClipProducerOnly:
      creatorStudioAiProducerEnableClipProducerOnly ?? true,
    creatorStudioAiProducerEnablePhotoTab:
      creatorStudioAiProducerEnablePhotoTab ?? true,
    creatorStudioCaptionsEnableArborTemplates:
      creatorStudioCaptionsEnableArborTemplates ?? true,
    creatorStudioMediaEnableTab: creatorStudioMediaEnableTab ?? true,
    creatorStudioTimelineEnableFit13: creatorStudioTimelineEnableFit13 ?? true,
    creatorStudioTimelineEnableFitFill:
      creatorStudioTimelineEnableFitFill ?? true,
    creatorStudioTimelineEnableFitSplitHorizontal11:
      creatorStudioTimelineEnableFitSplitHorizontal11 ?? true,
    creatorStudioTimelineEnableFitSplitHorizontal13:
      creatorStudioTimelineEnableFitSplitHorizontal13 ?? true,
    creatorStudioTimelineEnableFitSplitVertical11:
      creatorStudioTimelineEnableFitSplitVertical11 ?? true,
    creatorStudioTimelineEnableFitZoomCall:
      creatorStudioTimelineEnableFitZoomCall ?? true,
    creatorStudioTimelineEnableLockFraming:
      creatorStudioTimelineEnableLockFraming ?? true,
    creatorStudioTimelineEnableReframingDropdown:
      creatorStudioTimelineEnableReframingDropdown ?? true,
    creatorStudioSettingsAnimationAndAudioFadeOnly:
      creatorStudioSettingsAnimationAndAudioFadeOnly ?? false,
    creatorStudioFontsHideArborFonts: creatorStudioFontsHideArborFonts ?? false,
    storyManagerAddStoriesEnableButtons:
      storyManagerAddStoriesEnableButtons ?? true,
    enableFramingTab: enableFramingTab || false,
    enableClipProducerVideoTemplateSelection:
      enableClipProducerVideoTemplateSelection ?? false,
    enableMovingBetweenTracks: enableMovingBetweenTracks || false,
    contentGenerationChatGptModel: contentGenerationChatGptModel || undefined,
    contentStudioQuotesHide169And916: contentStudioQuotesHide169And916 ?? false,
    // Translation reviewers also get the clip preview's Transcript subtab — it
    // is the surface reviewer mode exists for. Without this, reviewer mode
    // hides the other four subtabs and this flag's `false` default hides the
    // fifth, leaving an empty tab row over an empty pane.
    contentStudioModalEnableTranscript:
      reviewerMode || (contentStudioModalEnableTranscript ?? false),
    contentStudioModalEnableComments: contentStudioModalEnableComments ?? false,
    // Nested inside the comments flag: a drawing is attached to a comment, so
    // drawing without commenting has nothing to attach to (PLAT-11284).
    contentStudioEnableCommentDrawing:
      (contentStudioModalEnableComments ?? false) &&
      (contentStudioEnableCommentDrawing ?? false),
    enableRendererLayoutReorder: enableRendererLayoutReorder ?? true,
    enableTimelineCopyPaste: enableTimelineCopyPaste ?? false,
    contentStudioEnableBlockTempClipEditing:
      contentStudioEnableBlockTempClipEditing ?? false,
    contentStudioPullQuotesEnablePhotoSources:
      contentStudioPullQuotesEnablePhotoSources ?? false,
    enablePostHogAiObservability: enablePostHogAiObservability ?? false,
    contentStudioContentProducingEnableGenerate:
      contentStudioContentProducingEnableGenerate ?? true,
    creatorStudioLayoutStrategy:
      typeof creatorStudioLayoutStrategy === 'string'
        ? creatorStudioLayoutStrategy
        : 'hybrid',
    creatorStudioTimelineWindowingConfig:
      creatorStudioTimelineWindowingConfig ?? undefined,
    enableClipProducerReels: enableClipProducerReels ?? false,
    homeActionsEnableEmailButton: homeActionsEnableEmailButton ?? true,
    homeActionsEnableEditVideoButton: homeActionsEnableEditVideoButton ?? true,
    homeActionsEnableAddStoryButton: homeActionsEnableAddStoryButton ?? true,
    creatorStudioTranscriptAddPhoto: creatorStudioTranscriptAddPhoto ?? true,
    contentStudioTranscriptAddPhoto: contentStudioTranscriptAddPhoto ?? true,
    // Single gate for "Add Overlay Text & Graphic" from a transcript selection
    // across BOTH surfaces (Creator Studio + Content Studio clip preview).
    // Replaces the former per-surface flags. Defaults `false` (dark launch) —
    // enabled via LaunchDarkly when ready.
    enableTranscriptOverlayAdd: enableTranscriptOverlayAdd ?? false,
    enableTranscriptOverlayRangeAdjust:
      enableTranscriptOverlayRangeAdjust ?? false,
    // Single gate for the composition canvas quick-edit action menu
    // (Canva-style pencil/trash pill + quick-edit popover, PLAT-9968 /
    // PLAT-10211) across BOTH surfaces (Creator Studio canvas + Content
    // Studio clip preview). Defaults `true` to preserve the current
    // always-on behavior; flip off via LaunchDarkly to disable.
    enableCompositionCanvasQuickEdit: enableCompositionCanvasQuickEdit ?? true,
    enableTranslationReview: enableTranslationReview ?? false,
    // Single gate for undo/redo while a TRANSLATED language is on screen,
    // across BOTH studios: the Content Studio transcript header (clip
    // preview/editor + Captions tab) and Creator Studio's player controls.
    // The undo stack doesn't cover translated edits, so the buttons are
    // misleading there; flip this off in LaunchDarkly to hide them. Defaults
    // `true` to preserve current behavior; Original-language transcripts are
    // unaffected either way.
    //
    // The `contentStudio` prefix is a misnomer the key is stuck with — the LD
    // key `content-studio-enable-translated-undo-redo` predates the Creator
    // Studio half and keys can't be renamed. Don't "fix" this name: it must
    // stay the camelCase of the key or the flag reads as `undefined`.
    contentStudioEnableTranslatedUndoRedo:
      contentStudioEnableTranslatedUndoRedo ?? true,
    // Translation Reviewer reduced experience (PLAT): dark-launched off,
    // targeted at reviewer emails / an LD segment. Drives the stripped-down
    // nav, tab, and read-only-transcript gating across the app.
    enableTranslationReviewerMode: reviewerMode,
    enableWhatsNewChangelog: enableWhatsNewChangelog ?? false,
    enableSettingsScreen: enableSettingsScreen ?? false,
    // Gates the Settings → "AI Voices" second-level tab (AI dubbing voice
    // activation). Dark-launched off; the sibling "Social" tab (Distribution
    // Channels) always shows. Turned on via LaunchDarkly where dubbing rolls
    // out. (PLAT-10545)
    enableAiVoices: enableAiVoices ?? false,
    // PLAT-11797 — the panel of a voice model's own numeric and on/off controls
    // in the dub popover, offered instead of a written direction for the models
    // that take those controls. Dark-launched off: a wrong setting produces an
    // unlistenable take, so it must be revocable without a deploy. The markers
    // editor (PLAT-11798) is deliberately NOT behind this flag — markers are
    // the only way the ElevenLabs v3 model can be directed at all, so gating
    // them would leave that model undirectable.
    enableDubVoiceSettings: enableDubVoiceSettings ?? false,
    // PLAT-11678 — in Translation Review, a saved translation edit re-dubs its
    // segment automatically. Default ON (decided with Roman, Sep 11, 2026);
    // LaunchDarkly can turn it off, which leaves edits saving as before with
    // no dub-side reaction.
    enableAutoRedubOnEdit: enableAutoRedubOnEdit ?? true,
    // Gates the Settings → "Translation Memory" second-level tab (workspace
    // glossary curation). Dark-launched off; turned on via LaunchDarkly where
    // translation memory rolls out. (PLAT-10844)
    enableTranslationMemory: enableTranslationMemory ?? false,
    // Gates the launch-period splitter picker (lalal.ai | ElevenLabs) on the
    // voice/music Separate control (PLAT-10622, DE4). Dark-launched off; steady
    // state hides the picker and uses the backend env-default splitter
    // (server-chosen — the frontend does not assume which provider).
    enableStemSplitterPicker: enableStemSplitterPicker ?? false,
    // Cap (minutes) for voice/music separation (PLAT-10626). Default 20;
    // overridable via LaunchDarkly to raise/lower the allowed track length.
    stemSeparationMaxMinutes:
      typeof stemSeparationMaxMinutes === 'number'
        ? stemSeparationMaxMinutes
        : DEFAULT_STEM_SEPARATION_MAX_MINUTES,
    // Cap (minutes) on the video length a dub may cover (PLAT-11532). Default
    // 10; overridable via LaunchDarkly once a real run has been timed.
    dubbingMaxMinutes:
      typeof dubbingMaxMinutes === 'number'
        ? dubbingMaxMinutes
        : DEFAULT_DUBBING_MAX_MINUTES,
    enableStoryFolders: enableStoryFolders ?? false,
    enableSpeakerManager: enableSpeakerManager ?? false,
    lockSceneBoundElements: lockSceneBoundElements ?? true,
    enableSafeZones: enableSafeZones ?? false,
    videoTemplatesHideSettings: videoTemplatesHideSettings ?? false,
    // Gates the per-element "Lock Position" toggles in the video-template update
    // modal (combined with admin privilege). Defaults `false` (dark launch).
    enableTemplateLockPosition: enableTemplateLockPosition ?? false,
    // Gates the per-clip review Status + Assignee controls on the Content
    // Studio video clip cards and their filter sections (PLAT-10480).
    // Defaults `false` (dark launch) — enabled via LaunchDarkly when ready.
    contentStudioEnableClipStatusAssignee:
      contentStudioEnableClipStatusAssignee ?? false,
    // Gates basic reel editing end to end (epic PLAT-11048): multi-select on
    // the clip cards, the bulk action bar and Save as Reel (PLAT-11049), plus
    // the Reel Editor tab in the clip preview (PLAT-11051). One flag for the
    // whole gesture — a creator who can build a reel can always reshape it.
    // Defaults `false` (dark launch); it must stay off until PLAT-11062 has
    // renamed the transcript's speaker "Section" copy to "Selection", so the
    // two meanings of the word are never live at once (DE2).
    enableBasicReelCreation: enableBasicReelCreation ?? false,
    // Gates the Delete action in the Content Studio clip bulk action bar, and
    // with it the widened selection — any saved clip becomes selectable, not
    // only one a reel can be built from (PLAT-11118). Its own flag rather than
    // riding `enableBasicReelCreation`: deleting is not reel creation, and a
    // destructive group action wants to roll out on its own.
    // Defaults `false` (dark launch).
    enableBulkClipDelete: enableBulkClipDelete ?? false,
    // PLAT-11155 — the reel's Chapters and Sections drawn into the clip
    // preview's transcript (headings, then collapse and navigation). Its own
    // flag rather than riding `enableBasicReelCreation`: the Reel Editor is
    // live while this reading is still proving itself, and it must be able to
    // roll back without taking reel creation down. Defaults `false` (dark
    // launch). Read per surface; PLAT-11165 reads it for Creator Studio.
    enableReelTranscriptStructure: enableReelTranscriptStructure ?? false,
  };
}
